<template>
  <div class="data-view">
    <div class="query-section">
      <h2>Query Your Data</h2>
      <p class="subtitle">Ask questions about your data in natural language</p>

      <div class="query-input-container">
        <textarea
          v-model="queryText"
          placeholder="e.g., Show me all records from last week"
          rows="3"
          @keydown.ctrl.enter="executeQuery"
        />
        <button
          @click="executeQuery"
          :disabled="!queryText.trim() || isLoading"
          class="execute-button"
        >
          {{ isLoading ? "Querying..." : "Run Query" }}
        </button>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-if="tableName" class="queried-table">
        Querying table: <strong>{{ tableName }}</strong>
      </div>
    </div>

    <div v-if="hasQueried && results.length === 0 && !error" class="results-section empty-results">
      <p>No results found. Try adjusting your query or check that the table has data.</p>
    </div>

    <div v-if="results.length > 0" class="results-section">
      <div class="results-header">
        <h3>Results ({{ rowCount }} rows)</h3>
        <button @click="exportToCsv" class="export-button">Export to CSV</button>
      </div>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th v-for="col in columns" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in results" :key="idx">
              <td v-for="col in columns" :key="col">
                {{ formatValue(row[col]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import api from "@/Modules/ETL/services/api";
import Papa from "papaparse";

const queryText = ref("");
const isLoading = ref(false);
const error = ref("");
const tableName = ref("");
const results = ref([]);
const rowCount = ref(0);
const hasQueried = ref(false);

const columns = computed(() => {
  if (results.value.length === 0) return [];
  return Object.keys(results.value[0]);
});

const executeQuery = async () => {
  if (!queryText.value.trim()) return;

  isLoading.value = true;
  error.value = "";
  tableName.value = "";
  results.value = [];
  hasQueried.value = false;

  try {
    const response = await api.queryData(queryText.value, 100);
    tableName.value = response.data.tableName || "";
    results.value = response.data.results;
    rowCount.value = response.data.rowCount;
    hasQueried.value = true;
  } catch (err) {
    error.value = err.errorMessage || "Failed to execute query";
  } finally {
    isLoading.value = false;
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const exportToCsv = () => {
  if (results.value.length === 0) return;

  // Convert results to CSV using papaparse
  const csv = Papa.unparse(results.value);

  // Create a blob and download link
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  link.href = url;
  link.download = `query-results-${timestamp}.csv`;

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
</script>

<style scoped>
.data-view {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.query-section {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
  margin-bottom: 2rem;
}

h2 {
  margin: 0 0 0.5rem 0;
  color: var(--color-text-primary);
}

.subtitle {
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem 0;
}

.query-input-container {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

textarea {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  color: var(--color-text-primary);
}

textarea:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.2);
}

.execute-button {
  padding: 0.75rem 1.5rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s;
}

.execute-button:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.execute-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-message {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-error-light);
  color: var(--color-error);
  border-radius: var(--radius-lg);
  border-left: 4px solid var(--color-error);
}

.queried-table {
  margin-top: 1rem;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.empty-results {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: var(--color-text-secondary);
  font-size: 14px;
}

.results-section {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.results-section h3 {
  margin: 0;
  color: var(--color-text-primary);
}

.export-button {
  padding: 0.5rem 1rem;
  background: var(--color-success);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s;
}

.export-button:hover {
  opacity: 0.9;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

thead {
  background: var(--color-background-light);
}

th {
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-bottom: 2px solid var(--color-border-light);
}

td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--color-border-light);
  color: var(--color-text-primary);
}

tbody tr:hover {
  background: var(--color-background-light);
}
</style>
